import * as React from 'react';
import { addDays, format, isEqual } from 'date-fns';

// Material-UI
import BlockIcon from '@mui/icons-material/Block';
import { blueGrey, green, red } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridColumnHeaderParams,
  GridOverlay
} from '@mui/x-data-grid';
import PendingIcon from '@mui/icons-material/Pending';
import Tooltip from '@mui/material/Tooltip';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  DateRange,
  RequestPass
} from '../../models';

import {
  EMPTY_STRING,
  PassStatusID
} from '../../global';

interface PassesByDateGridProps {
  dateRange: DateRange;
  days: number;
  isLoading: boolean;
  rows: Array<Array<RequestPass>>;
}

const PassesByDateGrid: React.FC<PassesByDateGridProps> = (props) => {

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      '& .super-app-theme--header': {
        backgroundColor: 'rgba(255, 7, 0, 0.55)'
      }
    },
    confirmed: {
      color: green[500]
    },
    deined: {
      color: red[500]
    },
    noRows: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2, 0, 2, 0)
    },
    pending: {
      color: blueGrey[500]
    }
  };

  // #endregion

  // #region Methods

  /*
 * Grid column definitions
 */
  const columns = (): Array<GridColDef> => {

    const dates: Array<GridColDef> = [];

    for (let i = 0; i < props.days; i++) {
      dates.push(
        {
          field: `date` + i.toString(),
          headerAlign: 'center',
          renderHeader: (params: GridColumnHeaderParams) => (
            renderHeader(params, i)
          ),
          align: 'left',
          width: 200,
          type: 'string',
          renderCell: (params: GridCellParams) => (
            renderPass(params, i)
          )
        }
      );
    }

    return [...dates];

  }

  /*
   * Overlay displayed when no data
   */
  const noRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={styles.noRows}>No requested passes for the selected date range</div>
      </GridOverlay>
    );
  }

  /*
   * Render the header for a date column
   */
  const renderHeader = (params: GridColumnHeaderParams, index: number) => {

    if (props.dateRange.startDate !== EMPTY_STRING && props.dateRange.endDate !== EMPTY_STRING) {
      const start = new Date(props.dateRange.startDate);
      const date = format(addDays(start, index), 'EEEEEE M/d');

      return (
        <span>{date}</span>
      );
    }

  }

  /*
   * Render pass information for row
   */
  const renderPass = (params: GridCellParams, index: number) => {

    const start = new Date(props.dateRange.startDate);
    const date = addDays(start, index);
    const passes = params.row as Array<RequestPass>;

    const p = passes.find(p => {
      return isEqual(new Date(p.passDate), date);
    });

    if (p !== undefined) {
      return (
        <><span>{p.rank}-{p.person}</span><span>&nbsp;</span><span>{renderStatus(p.statusID, p.status)}</span></>
      );
    } else {
      return (
        <span></span>
      );
    }

  }

  /*
   * Render the request status icon
   */
  const renderStatus = (statusID: number, status: string) => {

    switch (statusID) {
      case PassStatusID.Requested:
        return (
          <Tooltip title={status} arrow>
            <PendingIcon fontSize="medium" sx={styles.pending} />
          </Tooltip>
        )
      case PassStatusID.Confirmed:
        return (
          <Tooltip title={status} arrow>
            <CheckCircleIcon fontSize="medium" sx={styles.confirmed} />
          </Tooltip>
        )
      case PassStatusID.Denied:
        return (
          <Tooltip title={status} arrow>
            <BlockIcon fontSize="medium" sx={styles.deined} />
          </Tooltip>
        )
      default:
        return (<span />)
    }

  }

  // #endregion

  return (
    <DataGrid
      columns={columns()}
      rows={props.rows}
      getRowId={(row) => row[0].rank} // Special unique ID for each row
      sx={styles.root}
      density="compact"
      disableColumnSelector
      disableRowSelectionOnClick
      hideFooter
      loading={props.isLoading}
      slots={{
        noRowsOverlay: noRowsOverlay
      }}
    />
  );

}

export default PassesByDateGrid;